/**
 * What the assistant can *answer*, as opposed to what it can change.
 *
 * Reads and writes are kept structurally apart. A write goes through the
 * bulk-edit dry run and needs explicit confirmation; an answer only ever
 * produces text. Nothing in this module can stage a mutation, so a
 * misinterpreted question is a wrong sentence rather than a wrong listing.
 *
 * Adding a new answerable question is meant to be a data change: append a
 * {@link KnowledgeQuery} to {@link KNOWLEDGE_QUERIES} with the phrasings it
 * covers and a function that produces the answer. The conversation code does
 * not need to change.
 *
 * Answers may come from data already in hand (the bulk-edit export, passed in
 * via {@link KnowledgeContext.valueFor}) or from an API call. Prefer the former
 * — it costs nothing and cannot fail.
 */

import { containsFuzzyPhrase, tokenize, tokensSimilar } from './fuzzyMatch';
import { countActiveBookings } from '../location/requests/location-requests';
import { requestCalculatePayoutsSummary } from '../payouts/request/payouts-requests';
import { requestReadyForPayoutTransactionsForAccount } from '../../views/requests/account-report';
import { getSupplierAccountsReceivableSummary } from '../../requests/reports/invoices/supplier-accounts-receivable-requests';
import { formatMoneyAddDecimalPointAndThousands } from '../../util/PaymentUtils';
import type { LocationCandidate } from './locationResolver';
import type { LocationScope } from './locationScope';
import type { ResolvedPeriod } from './periodParser';
import type { AssistantAudience } from './assistantCapabilities';
import type { GeocodeFn } from './geocode';
import type { PlannerTool } from './planner/types';
import { apiDateRange } from './requests/apiDates';
import { topSupplierRevenueQuery } from './topRevenueSkill';
import {
  adminOveragesQuery,
  buyerOveragesQuery,
  supplierOveragesQuery,
} from './overagesSkill';
import { buyerOverageInvoiceAmountQuery } from './overageInvoiceAmountSkill';
import { whyBookingDeclinedQuery } from './declinedBookingSkill';
import { locationSearchQuery } from './locationSearchSkill';
import { bookingPaymentFailureQuery } from './bookingPaymentFailureSkill';
import { availableSpacesQuery } from './availableSpacesSkill';
import { priceBenchmarkQuery } from './priceBenchmarkSkill';
import { equipmentCheckoutQuery } from './equipmentCheckoutSkill';
import { bookingLifecycleQuery } from './bookingLifecycleSkill';
import { REPORT_DESCRIPTORS } from './reportCatalog';
import { buildReportCountQuery } from './reportSkills';

/** One "label: value" line rendered under an answer. */
export interface KnowledgeBreakdownRow {
  label: string;
  value: string;
  link?: KnowledgeLink;
}

/**
 * An optional call-to-action an answer can offer, navigating within the app when
 * clicked. Used by the marketplace search to send the user to a single matching
 * location's profile, or to the search page for a cluster of matches.
 */
export interface KnowledgeLink {
  label: string;
  /** An in-app path, e.g. `/location-profile/123` or `/search?lat=..&lng=..`. */
  path: string;
}

export interface KnowledgeAnswer {
  text: string;
  breakdown?: KnowledgeBreakdownRow[];
  link?: KnowledgeLink;
}

export interface KnowledgeContext {
  /** How the question was scoped: one named location, a status filter, or all. */
  scope: LocationScope;
  /** The locations the question applies to, already resolved. */
  targets: LocationCandidate[];
  /** True when the question named no location and so covers the whole account. */
  wholeAccount: boolean;
  /**
   * The time window the question asked about, already resolved to dates. Always
   * present: when the question named no period this is the default window, and
   * answers are expected to state {@link ResolvedPeriod.label} so the user can
   * see which window they actually got.
   */
  period: ResolvedPeriod;
  /** Current value of an export column for a location, or '' when absent. */
  valueFor: (locationId: string, column: string) => string;
  /**
   * The raw user message. Most skills answer from the resolved fields above, but
   * a few need the words themselves — the marketplace search needs the place to
   * look near, the payment-failure lookup needs the order number. Provided so
   * those skills need no parsing seam of their own.
   */
  message: string;
  /**
   * The signed-in account type, so a skill serving several audiences can pick
   * the right endpoint (a buyer's booking search differs from a supplier's).
   */
  audience: AssistantAudience;
  /**
   * Resolves a place description to coordinates, for skills that search the
   * marketplace by city or ZIP. Defaults to a geocoder that always declines, so
   * a skill degrades to "I could not find that place" wherever one is not wired
   * in (tests, or an account with no map loaded) rather than crashing.
   */
  geocode: GeocodeFn;
}

export interface KnowledgeQuery {
  id: string;
  /**
   * Account types this question can be answered for. Every shipped entry is
   * supplier-only because it runs against a supplier-scoped endpoint; a buyer
   * or admin skill simply declares its own audience here and needs no other
   * wiring.
   */
  audiences: AssistantAudience[];
  /**
   * Phrasings that identify this question. Matched longest-first, so a more
   * specific phrase wins over a general one that contains it.
   */
  synonyms: string[];
  /**
   * Concepts that together identify this question, as an optional fallback for
   * when no synonym matched.
   *
   * Every group must appear somewhere in the message, in any order — so
   * `[['most','top'], ['revenue','money']]` matches both "top revenue" and
   * "which one made the most money". Synonyms are contiguous phrases, which
   * loses to the combinatorics of natural phrasing: "what/which/who" x
   * "company/supplier/partner" x "made/earned" x "money/revenue" is hundreds of
   * sentences for one question, and enumerating them is a losing game.
   *
   * Deliberately only consulted **after** every synonym has failed, so this can
   * add matches where there were none but can never steal one from a skill that
   * matched precisely. Keep the groups narrow: a weak word like "made" would
   * pull in "who made the most bookings", which this skill cannot answer.
   */
  requires?: string[][];
  /** Shown when the assistant lists what it can answer. */
  example: string;
  /**
   * What this skill answers, written for a planner choosing between skills
   * rather than for a user. States the distinctions that phrase matching only
   * ever kept apart by luck — most importantly which of the money questions
   * means what — because a planner picking between two similar skills has
   * nothing else to go on.
   */
  description: string;
  answer: (context: KnowledgeContext) => Promise<KnowledgeAnswer>;
}

/**
 * Most locations a question will fan out over before it stops asking per
 * location. Each breakdown row is its own request, so a supplier with a large
 * portfolio would otherwise fire dozens of calls to render one answer.
 */
const MAX_BREAKDOWN_REQUESTS = 12;

const pluralize = (count: number, singular: string): string =>
  `${count} ${singular}${count === 1 ? '' : 's'}`;

/** Reads the page count out of the paginated search response. */
const readCount = (response: { body?: { count?: number } } | undefined): number =>
  response?.body?.count ?? 0;

interface PayoutSummary {
  /** Total paid out, in integer cents. */
  cents: number;
  /** Number of payouts making up that total. */
  payouts: number;
}

const readSummary = (
  response: { body?: { totalPayouts?: number; payoutsCount?: number } } | undefined,
): PayoutSummary => ({
  cents: response?.body?.totalPayouts ?? 0,
  payouts: response?.body?.payoutsCount ?? 0,
});

/**
 * One sentence describing a payout total.
 *
 * Zero gets its own wording — "$0.00" reads like a lookup that half worked,
 * whereas "nothing was paid out" reads like an answer. The payout count is
 * included because a supplier checking a figure wants to know whether it is one
 * large payout or many small ones.
 */
const describePayout = (
  summary: PayoutSummary,
  scopePhrase: string,
  period: ResolvedPeriod,
): string => {
  if (summary.cents === 0) {
    return `Nothing was paid out ${scopePhrase} ${period.label}.`;
  }

  return (
    `You were paid out **${formatMoneyAddDecimalPointAndThousands(summary.cents)}** ` +
    `${scopePhrase} ${period.label}, across ${pluralize(summary.payouts, 'payout')}.`
  );
};

const activeBookings: KnowledgeQuery = {
  id: 'activeBookings',
  audiences: ['supplier'],
  synonyms: [
    'how many bookings',
    'how many active bookings',
    'number of bookings',
    'number of active bookings',
    'active bookings',
    'bookings right now',
    'current bookings',
    'bookings do i have',
  ],
  example: 'how many bookings do I have right now',
  description:
    'The number of bookings that are active right now. A live count, not a ' +
    'historical total, so it ignores any time period in the question.',
  answer: async ({ targets, wholeAccount }) => {
    // One request covers the whole account. A status-filtered or named scope
    // has to be totalled per location, because the endpoint counts across the
    // account rather than across an arbitrary subset.
    const supplierBookingsLink: KnowledgeLink = { label: 'Open My Bookings', path: '/supplier-bookings' };

    if (wholeAccount) {
      const total = readCount(await countActiveBookings());
      return { text: `You have ${pluralize(total, 'active booking')} right now.`, link: supplierBookingsLink };
    }

    if (targets.length === 1) {
      const total = readCount(await countActiveBookings(targets[0].id));
      return {
        text: `**${targets[0].name}** has ${pluralize(total, 'active booking')} right now.`,
        link: supplierBookingsLink,
      };
    }

    if (targets.length > MAX_BREAKDOWN_REQUESTS) {
      const total = readCount(await countActiveBookings());
      return {
        text:
          `You have ${pluralize(total, 'active booking')} right now across every location. ` +
          `That is more than ${MAX_BREAKDOWN_REQUESTS} locations, so I have not broken it ` +
          'down — ask about one location for its own count.',
        link: supplierBookingsLink,
      };
    }

    const counts = await Promise.all(
      targets.map(async (target) => ({
        label: target.name,
        count: readCount(await countActiveBookings(target.id)),
      })),
    );

    const total = counts.reduce((sum, entry) => sum + entry.count, 0);
    return {
      text: `Those ${pluralize(targets.length, 'location')} have ${pluralize(
        total,
        'active booking',
      )} right now.`,
      breakdown: counts.map((entry) => ({ label: entry.label, value: String(entry.count) })),
      link: supplierBookingsLink,
    };
  },
};

const totalSpaces: KnowledgeQuery = {
  id: 'totalSpaces',
  audiences: ['supplier'],
  synonyms: [
    'how many spaces',
    'how much capacity',
    'how many total spaces',
    'total capacity',
    'total spaces',
    'how much space do i have',
  ],
  example: 'how many spaces do I have across all my locations',
  description:
    'The total number of parking spaces the supplier has listed \u2014 their ' +
    'capacity, not their occupancy or bookings. A property of the listings ' +
    'themselves, so it ignores any time period.',
  // Answered entirely from the export already fetched for this conversation,
  // so it costs no extra request and cannot fail independently.
  answer: async ({ targets, valueFor }) => {
    const counts = targets.map((target) => ({
      label: target.name,
      count: Number(valueFor(target.id, 'total_number_of_spaces')) || 0,
    }));

    const total = counts.reduce((sum, entry) => sum + entry.count, 0);

    if (counts.length === 1) {
      return { text: `**${counts[0].label}** has ${pluralize(counts[0].count, 'space')}.` };
    }

    return {
      text: `Those ${pluralize(counts.length, 'location')} have ${pluralize(
        total,
        'space',
      )} between them.`,
      breakdown: counts.map((entry) => ({ label: entry.label, value: String(entry.count) })),
    };
  },
};

/**
 * Total paid out to the supplier over a period, optionally per location.
 *
 * Sourced from the same summary endpoint as the Payouts report, so the number
 * the assistant gives reconciles exactly with the number the supplier sees on
 * that page. Amounts come back as integer cents.
 *
 * Worth being precise in the wording: this is money *paid out* to the supplier,
 * net of SecurSpace's fees — not gross booking value. Users say "revenue", so
 * the question matches on that word, but the answer says "paid out" rather than
 * quietly letting a net figure pass as a gross one.
 */
const revenue: KnowledgeQuery = {
  id: 'revenue',
  audiences: ['supplier'],
  synonyms: [
    'total revenue',
    'how much revenue',
    // Covers "how much did Toledo make last month", where the location name
    // sits inside the phrase and so breaks the contiguous forms below. Shorter
    // than the capacity synonyms ("how much capacity", "how much space do i
    // have"), which therefore still win under longest-match.
    'how much did',
    'how much did i make',
    'how much did i earn',
    'how much money did i make',
    'how much money did i earn',
    'how much have i made',
    'how much have i earned',
    'how much was i paid',
    'how much did i get paid',
    'total earnings',
    'total payouts',
    'my revenue',
    'my earnings',
    'my payouts',
    'revenue',
    // Deliberately no bare "earnings" or "payouts": at the slack fuzzy matching
    // allows, "earnings" sits one edit from "warnings", and a bare "payouts"
    // would answer a total for someone asking when their next payout lands.
  ],
  example: 'what was my total revenue across all locations last month',
  description:
    'Money already paid out to the supplier over a time period. This is what ' +
    'they earned and received, net of SecurSpace fees. Use for revenue, ' +
    'earnings or income questions about a past period. Not money still owed ' +
    'or still awaiting a payout run.',
  answer: async ({ targets, wholeAccount, period }) => {
    const range = apiDateRange(period);

    if (wholeAccount) {
      const summary = readSummary(await requestCalculatePayoutsSummary(range));
      return { text: describePayout(summary, 'across all your locations', period) };
    }

    if (targets.length === 1) {
      const summary = readSummary(
        await requestCalculatePayoutsSummary({ ...range, locationId: targets[0].id }),
      );
      return { text: describePayout(summary, `for **${targets[0].name}**`, period) };
    }

    if (targets.length > MAX_BREAKDOWN_REQUESTS) {
      // The endpoint totals per location or per account, with nothing in
      // between, so a subset larger than the cap cannot be answered exactly.
      // Say plainly that the figure widened rather than labelling an
      // account-wide total as if it were the subset's.
      const summary = readSummary(await requestCalculatePayoutsSummary(range));
      return {
        text:
          `${describePayout(summary, 'across all your locations', period)} ` +
          `You asked about ${targets.length} locations, which is more than the ` +
          `${MAX_BREAKDOWN_REQUESTS} I will break down at once, so that total covers ` +
          'every location. Ask about one location for its own figure.',
      };
    }

    const summaries = await Promise.all(
      targets.map(async (target) => ({
        label: target.name,
        cents: readSummary(
          await requestCalculatePayoutsSummary({ ...range, locationId: target.id }),
        ).cents,
      })),
    );

    const total = summaries.reduce((sum, entry) => sum + entry.cents, 0);
    return {
      text:
        `Those ${pluralize(targets.length, 'location')} were paid out ` +
        `**${formatMoneyAddDecimalPointAndThousands(total)}** ${period.label}.`,
      breakdown: summaries.map((entry) => ({
        label: entry.label,
        value: formatMoneyAddDecimalPointAndThousands(entry.cents),
      })),
    };
  },
};

/**
 * Most ready-for-payout rows totalled in one request.
 *
 * Unlike the payouts and receivables summaries there is no server-side total for
 * this report — the Ready for Payout page itself sums the rows it happens to
 * have on screen — so the assistant pulls a page and adds them up. The cap keeps
 * that from turning into an unbounded download; `count` tells us when it bit,
 * and the answer says so rather than quietly under-reporting.
 */
const READY_FOR_PAYOUT_PAGE = 500;

/**
 * Money the buyer has already paid that is waiting on the next payout run.
 *
 * Deliberately has no period: the underlying report exposes no date filter, and
 * the question is inherently "as of now" anyway — a balance, not a flow. Asking
 * for it "last month" would be answered with the current balance, so the reply
 * always says "right now" to make that explicit.
 */
const readyForPayout: KnowledgeQuery = {
  id: 'readyForPayout',
  audiences: ['supplier'],
  synonyms: [
    'ready for payout',
    'ready to be paid out',
    'could be paid out',
    'could potentially be paid out',
    'waiting to be paid out',
    'pending payout',
    'pending payouts',
    'awaiting payout',
    'how much am i owed',
    'how much do i have coming',
    'upcoming payout',
    'next payout',
  ],
  example: 'how much is ready for payout right now',
  description:
    'Money the buyer has already paid that is waiting on the next payout run. ' +
    'It is owed to the supplier and its arrival is not in doubt, only its ' +
    'timing. A current balance with no time period. Distinct from revenue ' +
    '(already received) and from receivables (buyer has not paid yet).',
  answer: async ({ targets, wholeAccount }) => {
    const query: Record<string, string | number> = {
      page: 0,
      size: READY_FOR_PAYOUT_PAGE,
      sortBy: 'INVOICE_NUMBER',
      sortDir: 'asc',
    };
    if (!wholeAccount && targets.length === 1) query.locationId = targets[0].id;

    const response = await requestReadyForPayoutTransactionsForAccount(query);
    const rows: { payoutAmount?: number }[] = response?.body?.content ?? [];
    const count: number = response?.body?.count ?? rows.length;
    const cents = rows.reduce((sum, row) => sum + (row.payoutAmount ?? 0), 0);

    // Scoping to several locations at once would need one request per location
    // and this endpoint returns rows rather than totals, so it is not worth it —
    // say the figure is account-wide instead of implying it is the subset's.
    const scopePhrase =
      !wholeAccount && targets.length === 1
        ? `for **${targets[0].name}**`
        : 'across all your locations';

    if (cents === 0) {
      return { text: `Nothing is ready for payout ${scopePhrase} right now.` };
    }

    const truncated =
      count > rows.length
        ? ` That covers the first ${rows.length} of ${count} invoices, so the real figure is higher.`
        : '';

    return {
      text:
        `**${formatMoneyAddDecimalPointAndThousands(cents)}** is ready for payout ` +
        `${scopePhrase} right now, across ${pluralize(rows.length, 'invoice')}.` +
        truncated,
    };
  },
};

/**
 * Invoiced but not yet paid by the buyer — money owed to the supplier that
 * cannot be paid out yet.
 *
 * Distinct from {@link readyForPayout} on purpose: conflating "the buyer still
 * owes this" with "this is waiting on our next payout run" would tell a supplier
 * cash is coming when it is actually stuck with a customer. The aged buckets are
 * included because a receivables total without ageing hides the only thing that
 * makes it actionable.
 */
const outstandingReceivables: KnowledgeQuery = {
  id: 'outstandingReceivables',
  audiences: ['supplier'],
  synonyms: [
    'outstanding receivables',
    'accounts receivable',
    'total outstanding',
    'how much is outstanding',
    'how much am i owed by buyers',
    'unpaid invoices',
    'outstanding invoices',
    'how much is unpaid',
    'still owed',
  ],
  example: 'how much is outstanding from last month',
  description:
    'Invoiced money the buyer has not paid yet, with ageing buckets. This is ' +
    'debt owed by buyers and it may never arrive. Never conflate with ' +
    'ready-for-payout, which the buyer has already paid.',
  answer: async ({ targets, wholeAccount, period }) => {
    const params: Record<string, string> = { ...apiDateRange(period) };
    if (!wholeAccount && targets.length === 1) params.locationId = targets[0].id;

    const body = (await getSupplierAccountsReceivableSummary(params))?.body ?? {};
    const cents: number = body.totalOutstanding ?? 0;
    const invoices: number = body.invoices ?? 0;

    const scopePhrase =
      !wholeAccount && targets.length === 1
        ? `for **${targets[0].name}**`
        : 'across all your locations';

    if (cents === 0) {
      return { text: `Nothing is outstanding ${scopePhrase} ${period.label}.` };
    }

    const buckets: KnowledgeBreakdownRow[] = (
      [
        ['Current (0-30 days)', body.current],
        ['31-60 days', body.thirtyOneTo60],
        ['61-90 days', body.sixtyOneTo90],
        ['Over 90 days', body.over90],
      ] as [string, number | undefined][]
    )
      .filter(([, amount]) => (amount ?? 0) > 0)
      .map(([label, amount]) => ({
        label,
        value: formatMoneyAddDecimalPointAndThousands(amount as number),
      }));

    return {
      text:
        `**${formatMoneyAddDecimalPointAndThousands(cents)}** is outstanding ` +
        `${scopePhrase} ${period.label}, across ${pluralize(invoices, 'invoice')}. ` +
        'That is invoiced but not yet paid by the buyer, so it is not ready for payout yet.',
      breakdown: buckets.length > 0 ? buckets : undefined,
    };
  },
};

/**
 * Why a supplier has not seen a payout land.
 *
 * A diagnostic explainer, not a figure. It fell back to Gemini as
 * "why have I not received money", which then guessed at an invoice search —
 * a payout question answered with the wrong report. The honest answer names the
 * three real causes and hands the user the two existing skills that quantify the
 * first two, plus the settings page behind the third. Static text: it reads no
 * data, so it cannot fail or mislead with a stale number.
 */
const whyNotPaid: KnowledgeQuery = {
  id: 'whyNotPaid',
  audiences: ['supplier'],
  synonyms: [
    'why have i not received money',
    'why have i not received any money',
    'why have i not received a payout',
    'why have i not received my payout',
    'why have i not been paid',
    'why havent i been paid',
    'why have i not gotten paid',
    'why havent i received money',
    'why havent i gotten paid',
    'why have i not gotten my money',
    'why no payout',
    'why havent i received a payout',
    'where is my money',
    'where is my payout',
    'where are my payouts',
    'i have not been paid',
    'i havent been paid',
    'i have not received money',
    'i have not received my payout',
    'have not received a payout',
    'havent gotten paid',
    'never got paid',
    'missing payout',
    'missing my payout',
  ],
  requires: [
    ['why', 'where', 'not', 'havent', 'no', 'never', 'missing'],
    ['received', 'receive', 'paid', 'pay', 'get', 'got', 'gotten', 'seen'],
    ['money', 'payout', 'payouts', 'payment', 'deposit', 'deposited', 'funds'],
  ],
  example: 'why have i not received money',
  description:
    'Explains why a supplier may not have seen a payout land: the buyer has not ' +
    'paid yet (an outstanding receivable), the money is waiting on the next ' +
    'payout run (ready for payout), or the payout bank account is not connected. ' +
    'A diagnostic explainer, not a figure — distinct from the payout, ' +
    'ready-for-payout and receivables skills, which each return a number.',
  answer: async () => ({
    text:
      'Payouts happen in two steps: when a buyer pays an invoice the money first ' +
      'clears as a **receivable**, then becomes **ready for payout**, and is ' +
      'deposited to your connected bank on the next scheduled payout run. If you ' +
      'have not seen a deposit, it is usually one of three things:\n\n' +
      '1. **The buyer has not paid yet** — it is still an outstanding receivable, ' +
      'so there is nothing to pay out. Ask _"how much is outstanding"_ to see it.\n' +
      '2. **It is waiting on the next payout run** — the money is ready for payout ' +
      'but has not been sent yet. Ask _"how much is ready for payout"_ to see it.\n' +
      '3. **Your payout bank account is not fully connected** — payouts cannot be ' +
      'sent until it is. Check **Account → Payout Bank Account**.\n\n' +
      'If none of those explain it, contact SecurSpace support and we can trace ' +
      'the specific payout.',
  }),
};

/*
 * Hand-written skills come first, then the report counts generated from
 * descriptors. Order matters only for how the assistant lists what it can
 * answer; selection is by longest match or by planner, not by position.
 *
 * The hand-written ones are hand-written because their *wording* carries
 * judgement a template would flatten — that a payout figure is net rather than
 * gross, that ready-for-payout and receivables are different money. Counting is
 * uniform enough to generate; meaning is not.
 */
export const KNOWLEDGE_QUERIES: KnowledgeQuery[] = [
  activeBookings,
  totalSpaces,
  availableSpacesQuery,
  revenue,
  readyForPayout,
  outstandingReceivables,
  whyNotPaid,
  topSupplierRevenueQuery,
  supplierOveragesQuery,
  buyerOveragesQuery,
  buyerOverageInvoiceAmountQuery,
  adminOveragesQuery,
  whyBookingDeclinedQuery,
  locationSearchQuery,
  priceBenchmarkQuery,
  bookingPaymentFailureQuery,
  equipmentCheckoutQuery,
  bookingLifecycleQuery,
  ...REPORT_DESCRIPTORS.map(buildReportCountQuery),
];

/** Words that open a question, used to tell a query apart from an instruction. */
const QUESTION_OPENERS = [
  'how',
  'what',
  'whats',
  'which',
  'when',
  'where',
  'who',
  'why',
  'do',
  'does',
  'did',
  'is',
  'are',
  'am',
  'was',
  'were',
  'have',
  'has',
];

/**
 * True when a message reads as a question rather than an instruction.
 *
 * The opening word is matched typo-tolerantly, so "ow many bookings do I have"
 * is still recognised. Deliberately not the only gate: "can you update the
 * capacity on Cheyenne" is phrased as a question but is plainly a write, so the
 * caller tries the instruction parser first and only falls back here.
 */
export const isQuestion = (message: string): boolean => {
  const trimmed = message.trim();
  if (trimmed.endsWith('?')) return true;

  const [opener] = tokenize(trimmed);
  if (!opener) return false;

  return QUESTION_OPENERS.some((candidate) => tokensSimilar(opener, candidate));
};

/**
 * Finds the question a message is asking, or null when none is recognised.
 *
 * Longest synonym wins so "how many active bookings" is not shortened to
 * "active bookings" if the two ever diverge.
 */
/** True when every required concept group appears somewhere in the message. */
const matchesConcepts = (tokens: string[], groups: string[][]): boolean =>
  groups.every((group) =>
    group.some((word) => tokens.some((token) => tokensSimilar(token, word))));

/**
 * Picks the best skill for a message from an explicit candidate list.
 *
 * Separated from {@link resolveQuery} so the synonym-beats-concepts ordering can
 * be tested directly. It cannot be tested through the registry as it stands: the
 * only skill with concept groups is admin-only, and no admin skill's synonyms
 * overlap its concepts, so a registry-level test of that ordering would pass
 * whatever the code did.
 */
export const resolveAmong = (
  message: string,
  candidates: KnowledgeQuery[],
): KnowledgeQuery | null => {
  const tokens = tokenize(message);

  let bestQuery: KnowledgeQuery | null = null;
  let bestLength = 0;

  for (const query of candidates) {
    for (const synonym of query.synonyms) {
      if (synonym.length <= bestLength) continue;
      if (!containsFuzzyPhrase(tokens, tokenize(synonym))) continue;
      bestQuery = query;
      bestLength = synonym.length;
    }
  }

  if (bestQuery) return bestQuery;

  // Only reached when nothing matched precisely, so the looser concept pass can
  // rescue an unrecognised phrasing without ever overriding an exact match.
  let conceptQuery: KnowledgeQuery | null = null;
  let conceptGroups = 0;

  for (const query of candidates) {
    const groups = query.requires;
    if (!groups || groups.length <= conceptGroups) continue;
    if (!matchesConcepts(tokens, groups)) continue;
    // More required groups means a more specific claim on the question, so it
    // wins over a skill that asked for less.
    conceptQuery = query;
    conceptGroups = groups.length;
  }

  return conceptQuery;
};

export const resolveQuery = (
  message: string,
  audience: AssistantAudience,
): KnowledgeQuery | null => resolveAmong(message, queriesFor(audience));

/**
 * The skills offered to a planner for an account type.
 *
 * Filtered by audience **before** the planner ever sees it, so a planner cannot
 * select a skill the account is not entitled to even if it tries. Carries no
 * endpoint or parameters — a planner names a skill and {@link queryById} looks
 * up the executable part locally.
 */
export const toolManifest = (audience: AssistantAudience): PlannerTool[] =>
  queriesFor(audience).map((query) => ({
    id: query.id,
    description: query.description,
    example: query.example,
  }));

/**
 * Resolves a planner's chosen id back to a runnable skill.
 *
 * Re-checks the audience rather than trusting the caller to have filtered the
 * manifest, so this stays safe as the single execution entry point.
 */
export const queryById = (
  id: string,
  audience: AssistantAudience,
): KnowledgeQuery | null => queriesFor(audience).find((query) => query.id === id) ?? null;

/** The questions answerable for an account type. */
export const queriesFor = (audience: AssistantAudience): KnowledgeQuery[] =>
  KNOWLEDGE_QUERIES.filter((query) => query.audiences.includes(audience));

/**
 * Lists what the assistant can currently answer, for the "I don't know" reply.
 * Returns an empty string when nothing is answerable for this account type, so
 * callers can say so rather than trailing off after "I can answer".
 */
export const describeAnswerableQuestions = (audience: AssistantAudience): string =>
  queriesFor(audience)
    .map((query) => `"${query.example}"`)
    .join(', ');
